'use client';

import { useEffect, useState } from 'react';
import Image from 'next/image';
import { motion } from 'framer-motion';
import type { LucideIcon } from 'lucide-react';

type ColorType = 0 | 1 | 2;
type ButtonType = 'button' | 'submit' | 'reset';
type ButtonSize = 'xs' | 'md' | 'xl';

interface BreakPoints {
    md?: ButtonSize;
    lg?: ButtonSize;
    xl?: ButtonSize;
}

interface ButtonGAdaptiveProps {
    label?: string;
    description?: string;
    icon: LucideIcon | string;
    onClick?: () => void;
    className?: string;
    colorType?: ColorType;
    disabled?: boolean;
    type?: ButtonType;
    isProcessing?: boolean;
    size?: ButtonSize;
    breakpoints?: BreakPoints;
    // trueの場合、ラベル/description(hasText)が消えてアイコンのみになっても
    // アイコンラッパー・ボタンの高さを「テキスト有り」の時と同じサイズに固定する。
    // 画像アイコン(pc_bridge.png等)がラベル消失に合わせて縮んで見た目が崩れるのを防ぐためのオプトイン。
    lockIconSizeToText?: boolean;
}

const COLOR_STYLES = {
    0: {
        bg: 'bg-button-main',
        shadow: 'shadow-button-main',
        shadowHover: 'hover:shadow-button-main-hover',
        icon: 'text-color-button-main-icon',
        text: 'text-white',
    },
    1: {
        bg: 'bg-button-blue',
        shadow: 'shadow-button-blue',
        shadowHover: 'hover:shadow-button-blue-hover',
        icon: 'text-color-button-blue-icon',
        text: 'text-white',
    },
    2: {
        bg: 'bg-button-gray',
        shadow: 'shadow-button-gray',
        shadowHover: 'hover:shadow-button-gray-hover',
        icon: 'text-color-button-gray-icon',
        text: 'text-white',
    },
} as const;

const SIZE_STYLES = {
    xs: {
        layoutWithText: { gap: 8, paddingY: 4, paddingLeft: 16, paddingRight: 6 },
        layoutIconOnly: { size: 36 },
        label: { fontSize: 14, lineHeight: 1.2 },
        description: { fontSize: 12, marginBottom: 0, lineHeight: 1.2 },
        iconWrapperWithText: { size: 32 },
        iconWrapperIconOnly: { size: 28 },
        icon: { size: 20 },
        loadingSize: 18,
    },
    md: {
        layoutWithText: { gap: 12, paddingY: 6, paddingLeft: 24, paddingRight: 8 },
        layoutIconOnly: { size: 44 },
        label: { fontSize: 16, lineHeight: 1.3 },
        description: { fontSize: 12, marginBottom: 2, lineHeight: 1.3 },
        iconWrapperWithText: { size: 40 },
        iconWrapperIconOnly: { size: 36 },
        icon: { size: 24 },
        loadingSize: 22,
    },
    xl: {
        layoutWithText: { gap: 16, paddingY: 8, paddingLeft: 32, paddingRight: 8 },
        layoutIconOnly: { size: 56 },
        label: { fontSize: 16, lineHeight: 1.4 },
        description: { fontSize: 14, marginBottom: 2, lineHeight: 1.4 },
        iconWrapperWithText: { size: 48 },
        iconWrapperIconOnly: { size: 42 },
        icon: { size: 28 },
        loadingSize: 28,
    },
} as const;

export default function ButtonGAdaptive({
    label,
    description,
    icon: Icon,
    onClick,
    className = '',
    colorType = 0,
    disabled = false,
    type = 'button',
    isProcessing = false,
    size = 'xl',
    breakpoints = {},
    lockIconSizeToText = false,
}: ButtonGAdaptiveProps) {
    const [currentSize, setCurrentSize] = useState<ButtonSize>(size);

    useEffect(() => {
        const updateSize = () => {
            const width = window.innerWidth;
            if (width >= 1280 && breakpoints.xl) {
                setCurrentSize(breakpoints.xl);
            } else if (width >= 1024 && breakpoints.lg) {
                setCurrentSize(breakpoints.lg);
            } else if (width >= 768 && breakpoints.md) {
                setCurrentSize(breakpoints.md);
            } else {
                setCurrentSize(size);
            }
        };

        updateSize();
        window.addEventListener('resize', updateSize);
        return () => window.removeEventListener('resize', updateSize);
    }, [size, breakpoints]);

    const style = COLOR_STYLES[colorType];
    const isLocked = isProcessing;
    const hasText = Boolean(label) || Boolean(description);
    const labelWeight = currentSize === 'xs' ? 'font-medium' : 'font-bold';

    const sizeStyle = SIZE_STYLES[currentSize];
    const layoutWithText = sizeStyle.layoutWithText;
    const layoutIconOnly = sizeStyle.layoutIconOnly;
    const iconWrapperWithText = sizeStyle.iconWrapperWithText;
    const iconWrapperIconOnly = sizeStyle.iconWrapperIconOnly;
    const wrapperSize = hasText || lockIconSizeToText ? iconWrapperWithText.size : iconWrapperIconOnly.size;
    // pc_bridge.png等のマスコット画像はLucideアイコンより枠いっぱいに見せたいため、
    // 既存のBridgeStatusIcon(32/40=0.8)と同じ比率でラッパーに対して大きめに表示する。
    const imageIconSize = Math.round(wrapperSize * 0.8);
    // lockIconSizeToText時、アイコンのみ状態の正方形サイズも「テキスト有り」の高さ(ラッパー+上下padding)に揃える
    const iconOnlySize = lockIconSizeToText
        ? wrapperSize + layoutWithText.paddingY * 2
        : layoutIconOnly.size;

    if (disabled) {
        return (
            <motion.button
                type={type}
                disabled
                className={`
                    group relative select-none flex-shrink-0
                    ${hasText ? 'inline-flex' : 'flex'} items-center justify-center
                    rounded-full
                    bg-gray-300 text-gray-600
                    shadow-none cursor-not-allowed brightness-100
                    ${className}
                `}
                style={{
                    gap: hasText ? layoutWithText.gap : undefined,
                    paddingTop: hasText ? layoutWithText.paddingY : undefined,
                    paddingBottom: hasText ? layoutWithText.paddingY : undefined,
                    paddingLeft: hasText ? layoutWithText.paddingLeft : undefined,
                    paddingRight: hasText ? layoutWithText.paddingRight : undefined,
                    width: !hasText ? iconOnlySize : undefined,
                    height: !hasText ? iconOnlySize : undefined,
                }}
            >
                {hasText && (
                    <div className="flex flex-col text-left pr-1">
                        {label && (
                            <div
                                className={`font-corp ${labelWeight} tracking-widest whitespace-nowrap text-gray-400`}
                                style={{
                                    fontSize: sizeStyle.label.fontSize,
                                    lineHeight: sizeStyle.label.lineHeight,
                                }}
                            >
                                {label}
                            </div>
                        )}
                        {description && (
                            <div
                                className="font-cantarell font-medium text-gray-400"
                                style={{
                                    fontSize: sizeStyle.description.fontSize,
                                    marginBottom: sizeStyle.description.marginBottom,
                                    lineHeight: sizeStyle.description.lineHeight,
                                }}
                            >
                                {description}
                            </div>
                        )}
                    </div>
                )}

                <div
                    className="rounded-full flex items-center justify-center bg-gray-200"
                    style={{
                        width: wrapperSize,
                        height: wrapperSize,
                    }}
                >
                    {typeof Icon === 'string' ? (
                        <Image
                            src={Icon}
                            alt=""
                            width={imageIconSize}
                            height={imageIconSize}
                            className="object-contain grayscale opacity-60"
                        />
                    ) : (
                        <Icon
                            className="text-gray-400"
                            strokeWidth={1.8}
                            style={{
                                width: sizeStyle.icon.size,
                                height: sizeStyle.icon.size,
                            }}
                        />
                    )}
                </div>
            </motion.button>
        );
    }

    return (
        <motion.button
            type={type}
            onClick={isLocked ? undefined : onClick}
            disabled={isLocked}
            className={`
                group relative select-none flex-shrink-0
                ${hasText ? 'inline-flex' : 'flex'} items-center justify-center
                rounded-full
                ${style.bg} ${style.shadow}
                ${
                    !isLocked
                        ? `
                    transition-all duration-200 ease-out
                    origin-center
                    hover:brightness-[1.05]
                    ${style.shadowHover}
                    active:scale-[0.98]
                `
                        : ''
                }
                ${isLocked ? 'pointer-events-none cursor-not-allowed' : ''}
                ${className}
            `}
            whileTap={isLocked ? {} : { scale: 0.98, y: 2 }}
            style={{
                gap: hasText ? layoutWithText.gap : undefined,
                paddingTop: hasText ? layoutWithText.paddingY : undefined,
                paddingBottom: hasText ? layoutWithText.paddingY : undefined,
                paddingLeft: hasText ? layoutWithText.paddingLeft : undefined,
                paddingRight: hasText ? layoutWithText.paddingRight : undefined,
                width: !hasText ? iconOnlySize : undefined,
                height: !hasText ? iconOnlySize : undefined,
            }}
        >
            {hasText && (
                <div className="flex flex-col text-left pr-1">
                    {label && (
                        <div
                            className={`font-corp ${labelWeight} tracking-widest whitespace-nowrap ${style.text}`}
                            style={{
                                fontSize: sizeStyle.label.fontSize,
                                lineHeight: sizeStyle.label.lineHeight,
                            }}
                        >
                            {label}
                        </div>
                    )}
                    {description && (
                        <div
                            className={`font-cantarell font-medium ${style.text}`}
                            style={{
                                fontSize: sizeStyle.description.fontSize,
                                marginBottom: sizeStyle.description.marginBottom,
                                lineHeight: sizeStyle.description.lineHeight,
                            }}
                        >
                            {description}
                        </div>
                    )}
                </div>
            )}

            <motion.div
                className="rounded-full flex items-center justify-center bg-color-white-base duration-150"
                whileTap={isLocked ? {} : { scale: 0.95 }}
                style={{
                    width: wrapperSize,
                    height: wrapperSize,
                }}
            >
                {isProcessing ? (
                    <Image
                        src="/images/common/loading-circle.svg"
                        alt="Loading"
                        width={sizeStyle.loadingSize}
                        height={sizeStyle.loadingSize}
                        className="object-contain"
                        priority
                    />
                ) : typeof Icon === 'string' ? (
                    <Image
                        src={Icon}
                        alt=""
                        width={imageIconSize}
                        height={imageIconSize}
                        className="object-contain duration-150"
                    />
                ) : (
                    <Icon
                        className={`duration-150 ${style.icon}`}
                        strokeWidth={2.0}
                        style={{
                            width: sizeStyle.icon.size,
                            height: sizeStyle.icon.size,
                        }}
                    />
                )}
            </motion.div>
        </motion.button>
    );
}
